import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { db } from "../firebase";
import { doc, getDoc, Timestamp } from "firebase/firestore";

const initialProfileState = {
  profileName: "",
  dateOfBirth: null,
  address: {
    street: "",
    city: "",
    state: "",
    zip: "",
  },
  contactNumber: "",
  gender: "",
  relationshipToUser: "",
  emergencyContactName: "",
  emergencyContactNumber: "",
  emergencyContactRelationship: "",
  bloodType: "",
  allergiesAndAdverseReactions: "",
  medicalConditions: "",
  significantMedicalHistory: "",
  insuranceProvider: "",
  preferredHospital: "",
  preferredLanguage: "",
  primaryDoctor: "",
  primaryCaregiver: "",
  profilePictureURL: "",
  notes: "",
  userId: "", // Will be set to currentUser.uid for new profiles
};

const CareProfileEditPage = () => {
  const { profileId } = useParams(); // To get 'new' or an actual ID
  const navigate = useNavigate();
  const { currentUser, activeCareProfile, addCareProfile, updateCareProfile } =
    useAuth();

  const [profileData, setProfileData] = useState(initialProfileState);
  const [loading, setLoading] = useState(false);
  const [isNewProfile, setIsNewProfile] = useState(false);
  const [dateError, setDateError] = useState("");
  const dateInputRef = useRef(null);

  useEffect(() => {
    if (profileId === "new") {
      setIsNewProfile(true);
      setProfileData({
        ...initialProfileState,
        userId: currentUser?.uid, // Pre-fill owner
      });
    } else if (profileId) {
      setIsNewProfile(false);
      setLoading(true);
      const fetchProfile = async () => {
        try {
          const profileDocRef = doc(db, "careProfiles", profileId);
          const docSnap = await getDoc(profileDocRef);
          if (docSnap.exists()) {
            const data = docSnap.data();

            // **FIX**: Validate timestamp before setting state to prevent crash
            try {
              data.dateOfBirth?.toDate();
            } catch (e) {
              console.warn(
                "Invalid Firestore timestamp received for dateOfBirth. Clearing.",
                e,
              );
              data.dateOfBirth = null;
            }

            if (data.userId === currentUser?.uid) {
              setProfileData({
                ...initialProfileState,
                ...data,
                id: docSnap.id,
              });
            } else {
              console.error(
                "Attempted to load profile not belonging to current user.",
              );
              navigate("/profiles");
            }
          } else {
            console.error("No such profile document!");
            navigate("/profiles");
          }
        } catch (error) {
          console.error("Error fetching profile for edit: ", error);
        }
        setLoading(false);
      };
      if (currentUser?.uid) {
        fetchProfile();
      }
    } else if (activeCareProfile) {
      setIsNewProfile(false);
      const profileToLoad = { ...activeCareProfile };
      // **FIX**: Also validate timestamp from activeCareProfile
      try {
        profileToLoad.dateOfBirth?.toDate();
      } catch (e) {
        console.warn(
          "Invalid Firestore timestamp received from activeCareProfile. Clearing.",
          e,
        );
        profileToLoad.dateOfBirth = null;
      }
      setProfileData({ ...initialProfileState, ...profileToLoad });
    } else {
      console.log(
        "No profile ID, not new, and no active profile. Defaulting to new profile form.",
      );
      setIsNewProfile(true);
      setProfileData({
        ...initialProfileState,
        userId: currentUser?.uid,
      });
    }
  }, [profileId, currentUser, navigate, activeCareProfile]); // Removed db from dependencies as it's stable

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name.startsWith("address.")) {
      const addressField = name.split(".")[1];
      setProfileData((prev) => ({
        ...prev,
        address: {
          ...(prev.address || {}),
          [addressField]: value,
        },
      }));
    } else if (name === "dateOfBirth") {
      let processedValue = value;
      if (value) {
        const dateParts = value.split("-");
        let year = dateParts[0];

        if (year && year.length > 4) {
          year = year.slice(-4);
          dateParts[0] = year;
          processedValue = dateParts.join("-");
        }
      }
      setProfileData((prev) => ({ ...prev, [name]: processedValue }));
    } else {
      setProfileData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const validateDate = () => {
    const dateValue = profileData.dateOfBirth;

    // If the value is a Firestore Timestamp (or any non-string), we assume it was
    // already validated when originally saved and skip further string parsing.
    if (dateValue && typeof dateValue !== "string") {
      setDateError("");
      return true;
    }

    if (!dateValue) {
      setDateError("");
      return true;
    }

    const parts = dateValue.split("-");

    // This handles partially entered dates, e.g., "YYYY-MM-"
    if (parts.length < 3 || !parts[0] || !parts[1] || !parts[2]) {
      setDateError("Please enter a complete date.");
      return false;
    }

    const year = parts[0];
    if (year.length !== 4) {
      setDateError("Year must be 4 digits.");
      return false;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const yearNum = parseInt(year, 10);
    const currentYear = today.getFullYear();

    if (yearNum > currentYear) {
      setDateError("Year cannot be greater than the current year.");
      return false;
    }

    const dob = new Date(
      yearNum,
      parseInt(parts[1], 10) - 1,
      parseInt(parts[2], 10),
    );
    if (dob > today) {
      setDateError("Date of Birth cannot be in the future.");
      return false;
    }

    setDateError("");
    return true;
  };

  const handleDateBlur = () => {
    if (!validateDate()) {
      dateInputRef.current.focus();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!currentUser) {
      alert("You must be logged in to save a profile.");
      return;
    }

    if (!validateDate()) {
      dateInputRef.current.focus();
      return;
    }

    setLoading(true);
    // Normalise dateOfBirth: keep Timestamp as-is, convert string to Timestamp, allow null
    let dob = null;
    if (profileData.dateOfBirth) {
      if (typeof profileData.dateOfBirth === "string") {
        dob = Timestamp.fromDate(new Date(profileData.dateOfBirth));
      } else if (profileData.dateOfBirth.toDate) {
        dob = profileData.dateOfBirth; // already Timestamp
      }
    }

    const dataToSave = {
      ...profileData,
      address: profileData.address || initialProfileState.address,
      dateOfBirth: dob,
    };

    try {
      if (isNewProfile) {
        await addCareProfile(dataToSave);
        alert("Profile created successfully!");
        navigate("/profiles");
      } else {
        const { id, ...dataToUpdate } = dataToSave;
        await updateCareProfile(profileId || id, dataToUpdate);
        alert("Profile updated successfully!");
        navigate("/profiles");
      }
    } catch (error) {
      console.error("Error saving profile: ", error);
      alert(`Error saving profile: ${error.message}`);
    }
    setLoading(false);
  };

  if (loading && !isNewProfile && profileId !== "new") {
    return <div className="p-6">Loading profile details...</div>;
  }

  if (!currentUser) {
    return <div className="p-6">Please log in to manage profiles.</div>;
  }

  return (
    <div className="p-4 md:p-8 bg-white rounded-xl shadow-xl max-w-4xl mx-auto">
      <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 mb-6">
        {isNewProfile
          ? "Create New Family Health Profile"
          : "Edit Family Health Profile"}
      </h2>
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label
            htmlFor="profileName"
            className="block text-sm font-medium text-gray-700"
          >
            Profile Name
          </label>
          <input
            type="text"
            name="profileName"
            id="profileName"
            value={profileData.profileName || ""}
            onChange={handleChange}
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            required
          />
        </div>

        <div>
          <label
            htmlFor="dateOfBirth"
            className="block text-sm font-medium text-gray-700"
          >
            Date of Birth
          </label>
          <input
            ref={dateInputRef}
            type="date"
            name="dateOfBirth"
            id="dateOfBirth"
            value={(() => {
              const dob = profileData.dateOfBirth;
              if (!dob) return "";
              if (typeof dob === "string") return dob;
              try {
                return dob.toDate
                  ? dob.toDate().toISOString().split("T")[0]
                  : "";
              } catch (e) {
                console.warn(
                  "Invalid date in profile data, rendering empty.",
                  e,
                );
                return "";
              }
            })()}
            onChange={handleChange}
            onBlur={handleDateBlur}
            className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${dateError ? "border-red-500" : "border-gray-300"}`}
          />
          {dateError && (
            <p className="text-red-500 text-xs mt-1">{dateError}</p>
          )}
        </div>

        <fieldset className="border p-4 rounded-md">
          <legend className="text-lg font-medium text-gray-700 px-2">
            Address
          </legend>
          <div className="space-y-4 mt-2">
            <div>
              <label
                htmlFor="address.street"
                className="block text-sm font-medium text-gray-700"
              >
                Street
              </label>
              <input
                type="text"
                name="address.street"
                id="address.street"
                value={profileData.address?.street || ""}
                onChange={handleChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              />
            </div>
            <div>
              <label
                htmlFor="address.city"
                className="block text-sm font-medium text-gray-700"
              >
                City
              </label>
              <input
                type="text"
                name="address.city"
                id="address.city"
                value={profileData.address?.city || ""}
                onChange={handleChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              />
            </div>
            <div>
              <label
                htmlFor="address.state"
                className="block text-sm font-medium text-gray-700"
              >
                State
              </label>
              <input
                type="text"
                name="address.state"
                id="address.state"
                value={profileData.address?.state || ""}
                onChange={handleChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              />
            </div>
            <div>
              <label
                htmlFor="address.zip"
                className="block text-sm font-medium text-gray-700"
              >
                Zip Code
              </label>
              <input
                type="text"
                name="address.zip"
                id="address.zip"
                value={profileData.address?.zip || ""}
                onChange={handleChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              />
            </div>
          </div>
        </fieldset>

        <div>
          <label
            htmlFor="contactNumber"
            className="block text-sm font-medium text-gray-700"
          >
            Contact Number
          </label>
          <input
            type="text"
            name="contactNumber"
            id="contactNumber"
            value={profileData.contactNumber || ""}
            onChange={handleChange}
            className="mt-1 block w-full input-style"
          />
        </div>
        <div>
          <label
            htmlFor="gender"
            className="block text-sm font-medium text-gray-700"
          >
            Gender
          </label>
          <input
            type="text"
            name="gender"
            id="gender"
            value={profileData.gender || ""}
            onChange={handleChange}
            className="mt-1 block w-full input-style"
          />
        </div>
        <div>
          <label
            htmlFor="relationshipToUser"
            className="block text-sm font-medium text-gray-700"
          >
            Relationship to User
          </label>
          <input
            type="text"
            name="relationshipToUser"
            id="relationshipToUser"
            value={profileData.relationshipToUser || ""}
            onChange={handleChange}
            className="mt-1 block w-full input-style"
          />
        </div>

        <fieldset className="border p-4 rounded-md">
          <legend className="text-lg font-medium text-gray-700 px-2">
            Emergency Contact
          </legend>
          <div className="space-y-4 mt-2">
            <div>
              <label
                htmlFor="emergencyContactName"
                className="block text-sm font-medium text-gray-700"
              >
                Name
              </label>
              <input
                type="text"
                name="emergencyContactName"
                id="emergencyContactName"
                value={profileData.emergencyContactName || ""}
                onChange={handleChange}
                className="mt-1 block w-full input-style"
              />
            </div>
            <div>
              <label
                htmlFor="emergencyContactNumber"
                className="block text-sm font-medium text-gray-700"
              >
                Number
              </label>
              <input
                type="text"
                name="emergencyContactNumber"
                id="emergencyContactNumber"
                value={profileData.emergencyContactNumber || ""}
                onChange={handleChange}
                className="mt-1 block w-full input-style"
              />
            </div>
            <div>
              <label
                htmlFor="emergencyContactRelationship"
                className="block text-sm font-medium text-gray-700"
              >
                Relationship
              </label>
              <input
                type="text"
                name="emergencyContactRelationship"
                id="emergencyContactRelationship"
                value={profileData.emergencyContactRelationship || ""}
                onChange={handleChange}
                className="mt-1 block w-full input-style"
              />
            </div>
          </div>
        </fieldset>

        <fieldset className="border p-4 rounded-md">
          <legend className="text-lg font-medium text-gray-700 px-2">
            Medical Information
          </legend>
          <div className="space-y-4 mt-2">
            <div>
              <label
                htmlFor="bloodType"
                className="block text-sm font-medium text-gray-700"
              >
                Blood Type
              </label>
              <input
                type="text"
                name="bloodType"
                id="bloodType"
                value={profileData.bloodType || ""}
                onChange={handleChange}
                className="mt-1 block w-full input-style"
              />
            </div>
            <div>
              <label
                htmlFor="allergiesAndAdverseReactions"
                className="block text-sm font-medium text-gray-700"
              >
                Allergies & Adverse Reactions
              </label>
              <textarea
                name="allergiesAndAdverseReactions"
                id="allergiesAndAdverseReactions"
                value={profileData.allergiesAndAdverseReactions || ""}
                onChange={handleChange}
                rows="3"
                className="mt-1 block w-full input-style"
              ></textarea>
            </div>
            <div>
              <label
                htmlFor="medicalConditions"
                className="block text-sm font-medium text-gray-700"
              >
                Current Medical Conditions
              </label>
              <textarea
                name="medicalConditions"
                id="medicalConditions"
                value={profileData.medicalConditions || ""}
                onChange={handleChange}
                rows="3"
                className="mt-1 block w-full input-style"
              ></textarea>
            </div>
            <div>
              <label
                htmlFor="significantMedicalHistory"
                className="block text-sm font-medium text-gray-700"
              >
                Significant Medical History
              </label>
              <textarea
                name="significantMedicalHistory"
                id="significantMedicalHistory"
                value={profileData.significantMedicalHistory || ""}
                onChange={handleChange}
                rows="3"
                className="mt-1 block w-full input-style"
              ></textarea>
            </div>
          </div>
        </fieldset>

        <fieldset className="border p-4 rounded-md">
          <legend className="text-lg font-medium text-gray-700 px-2">
            Preferences & Providers
          </legend>
          <div className="space-y-4 mt-2">
            <div>
              <label
                htmlFor="insuranceProvider"
                className="block text-sm font-medium text-gray-700"
              >
                Insurance Provider
              </label>
              <input
                type="text"
                name="insuranceProvider"
                id="insuranceProvider"
                value={profileData.insuranceProvider || ""}
                onChange={handleChange}
                className="mt-1 block w-full input-style"
              />
            </div>
            <div>
              <label
                htmlFor="preferredHospital"
                className="block text-sm font-medium text-gray-700"
              >
                Preferred Hospital
              </label>
              <input
                type="text"
                name="preferredHospital"
                id="preferredHospital"
                value={profileData.preferredHospital || ""}
                onChange={handleChange}
                className="mt-1 block w-full input-style"
              />
            </div>
            <div>
              <label
                htmlFor="preferredLanguage"
                className="block text-sm font-medium text-gray-700"
              >
                Preferred Language
              </label>
              <input
                type="text"
                name="preferredLanguage"
                id="preferredLanguage"
                value={profileData.preferredLanguage || ""}
                onChange={handleChange}
                className="mt-1 block w-full input-style"
              />
            </div>
            <div>
              <label
                htmlFor="primaryDoctor"
                className="block text-sm font-medium text-gray-700"
              >
                Primary Doctor
              </label>
              <input
                type="text"
                name="primaryDoctor"
                id="primaryDoctor"
                value={profileData.primaryDoctor || ""}
                onChange={handleChange}
                className="mt-1 block w-full input-style"
              />
            </div>
            <div>
              <label
                htmlFor="primaryCaregiver"
                className="block text-sm font-medium text-gray-700"
              >
                Primary Support Person
              </label>
              <input
                type="text"
                name="primaryCaregiver"
                id="primaryCaregiver"
                value={profileData.primaryCaregiver || ""}
                onChange={handleChange}
                className="mt-1 block w-full input-style"
              />
            </div>
          </div>
        </fieldset>

        <div>
          <label
            htmlFor="profilePictureURL"
            className="block text-sm font-medium text-gray-700"
          >
            Profile Picture URL
          </label>
          <input
            type="text"
            name="profilePictureURL"
            id="profilePictureURL"
            value={profileData.profilePictureURL || ""}
            onChange={handleChange}
            placeholder="Enter URL for profile image"
            className="mt-1 block w-full input-style"
          />
        </div>
        <div>
          <label
            htmlFor="notes"
            className="block text-sm font-medium text-gray-700"
          >
            General Notes
          </label>
          <textarea
            name="notes"
            id="notes"
            value={profileData.notes || ""}
            onChange={handleChange}
            rows="4"
            className="mt-1 block w-full input-style"
          ></textarea>
        </div>

        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
          >
            {loading
              ? "Saving..."
              : isNewProfile
                ? "Create Profile"
                : "Save Changes"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CareProfileEditPage;
